/**
 * Did the person ARRIVE at the current location — follow a link to it, or
 * open the page on it — rather than step back/forward onto it?
 *
 * Why a page needs to know: a per-device preference (the last Settings tab,
 * the docs view mode) may fill a URL that is silent about it, but only on
 * arrival. A silent URL that Back returns to meant the DEFAULT when it was
 * written (the default is omitted from the URL), so applying the preference
 * there re-opens the tab or view the person just stepped back from — Back
 * looks broken. `useUrlEnum`'s `rememberAs` uses this hook for exactly that
 * (it applied the preference to every silent URL until 2026-10-02).
 *
 * Arrival = a PUSH navigation, or the location this component mounted on —
 * but only until it has shown another one: Back to the first entry is a POP
 * to the SAME location key and must not count as arriving again. A REPLACE is
 * not an arrival: in-page replaces are canonicalisation and refinement.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export function useIsArrival(): boolean {
	const location = useLocation();
	const navigationType = useNavigationType();
	const mountKey = useRef(location.key);
	const leftMount = useRef(false);
	useEffect(() => {
		if (location.key !== mountKey.current) leftMount.current = true;
	}, [location.key]);
	return navigationType === "PUSH" || (location.key === mountKey.current && !leftMount.current);
}
